/**
 * Web Component entry point for the Q&A widget.
 *
 * Importing this module registers the <legion-qa> custom element
 * so it can be used in any HTML page or framework that supports web components.
 *
 * @example
 * ```ts
 * import '@legionhandtech/lht-react-widgets/qa-web-component';
 *
 * // Then use in HTML:
 * // <legion-qa api-url="https://your-domain.com" auth-token="jwt" />
 * ```
 */

import { attachAuthBridge, type AuthBridgeHandle, AUTH_BRIDGE_OBSERVED_ATTRS } from "../shared/auth-bridge";

const QA_CSS_ATTRIBUTES = [
  "qa-primary-color",
  "qa-background-color",
  "qa-text-color",
  "qa-secondary-text-color",
  "qa-border-color",
  "qa-staff-badge-color",
  "qa-accepted-color",
  "qa-title-size",
  "qa-body-size",
  "qa-meta-size",
  "qa-font-family",
  "qa-card-padding",
  "qa-card-gap",
  "qa-border-radius",
  "qa-card-shadow",
  "qa-button-radius",
  "qa-vote-button-size",
] as const;

class LegionQAElement extends HTMLElement {
  public iframe: HTMLIFrameElement | null = null;
  private authBridge: AuthBridgeHandle | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes(): string[] {
    return [
      "api-url",
      
      "product-id",
      "username",
      "width",
      "height",
      "theme",
      "page-size",
      "show-search",
      "show-ask-button",
      ...QA_CSS_ATTRIBUTES,
    
      ...AUTH_BRIDGE_OBSERVED_ATTRS,
    ];
  }

  connectedCallback(): void {
    this.authBridge = attachAuthBridge(this, {
      iframeAccessor: () => this.iframe,
      baseUrl: this.bridgeBaseUrl,
    });
    this.render();
  }

  disconnectedCallback(): void {
    this.authBridge?.detach();
    this.authBridge = null;
  }

  attributeChangedCallback(
    _name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    if (oldValue !== newValue) {
      this.render();
    }
  }

  private get apiUrl(): string {
    return this.getAttribute("api-url") || window.location.origin;
  }

  private get productId(): string {
    return this.getAttribute("product-id") || "";
  }

  private get username(): string {
    return this.getAttribute("username") || "Anonymous";
  }

  private get widgetWidth(): string {
    return this.getAttribute("width") || "600";
  }

  private get widgetHeight(): string {
    return this.getAttribute("height") || "800";
  }

  private get theme(): string {
    return this.getAttribute("theme") || "light";
  }

  private get pageSize(): number {
    return parseInt(this.getAttribute("page-size") || "5", 10);
  }

  private get showSearch(): boolean {
    return this.getAttribute("show-search") !== "false";
  }

  private get showAskButton(): boolean {
    return this.getAttribute("show-ask-button") !== "false";
  }

  render(): void {
    if (!this.shadowRoot) return;

    const iframe = document.createElement("iframe");
    const url = new URL("/widget/qa", this.apiUrl);

    if (this.productId) {
      url.searchParams.set("productId", this.productId);
    }
    if (this.username) {
      url.searchParams.set("username", this.username);
    }
    url.searchParams.set("pageSize", String(this.pageSize));
    url.searchParams.set("theme", this.theme);
    url.searchParams.set("showSearch", String(this.showSearch));
    url.searchParams.set("showAskButton", String(this.showAskButton));

    QA_CSS_ATTRIBUTES.forEach((attr) => {
      const val = this.getAttribute(attr);
      if (val !== null && val !== undefined && String(val).length > 0) {
        url.searchParams.set(attr, val);
      }
    });

    iframe.src = url.toString();
    iframe.width = this.widgetWidth;
    iframe.height = this.widgetHeight;
    iframe.frameBorder = "0";
    iframe.scrolling = "auto";
    iframe.title = "Community Q&A Widget";

    iframe.onload = () => {
      this.authBridge?.postCurrentToken();
      this.dispatchEvent(
        new CustomEvent("load", {
          detail: { widget: "qa", theme: this.theme },
        })
      );
    };

    iframe.onerror = () => {
      if (!this.shadowRoot) return;
      this.shadowRoot.innerHTML = `<div style="padding:32px;text-align:center;color:#856404;background:#fff3cd;border:1px solid #ffeaa7;border-radius:8px;">Failed to load Q&A widget</div>`;
    };

    const style = document.createElement("style");
    style.textContent = `
      :host {
        display: block;
        width: ${this.widgetWidth}px;
        height: ${this.widgetHeight}px;
      }
      iframe {
        width: 100%;
        height: 100%;
        border: none;
        border-radius: 8px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        background: white;
      }
    `;

    this.shadowRoot.innerHTML = "";
    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(iframe);
    this.iframe = iframe;
  }

  private get bridgeBaseUrl(): string {
    return this.apiUrl;
  }
}

if (
  typeof customElements !== "undefined" &&
  !customElements.get("legion-qa")
) {
  customElements.define("legion-qa", LegionQAElement);
}

export { LegionQAElement };

